import { Button, Collapse, Flex, Grid, Paper, Select, TextInput } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { BroomIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { IReference } from "../../store/General"
import { BackOrderFilters } from "./back-order-filters"

type Props = {
  opened: boolean
  filters: BackOrderFilters
  setFilter: <K extends keyof BackOrderFilters>(key: K, value: BackOrderFilters[K]) => void
  clearFilters: () => void
  orderStatus: IReference[]
  paymentStatus: IReference[]
  deliveryNoteStatus: IReference[]
  invoiceStatus: IReference[]
  orderPurchaseTypes: IReference[]
  onSearch?: () => void
}

export function BackOrderFilterPanel({
  opened,
  filters,
  setFilter,
  clearFilters,
  orderStatus,
  paymentStatus,
  deliveryNoteStatus,
  invoiceStatus,
  orderPurchaseTypes,
  onSearch,
}: Props) {
  const { t } = useTranslation(["order", "common"])
  return (
    <Collapse expanded={opened}>
      <Paper p="md" mb="sm" withBorder radius="md" style={{ background: "var(--mantine-color-gray-0)" }}>
        <Grid>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <TextInput
              label="PO №"
              value={filters.purchaseCode}
              onChange={(e) => setFilter("purchaseCode", e.target.value)}
              placeholder={t("common:actions.search")}
              size="sm"
            />
          </Grid.Col>

          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <DatePickerInput
              label={t("order:backOrder.poDate")}
              type="range"
              value={filters.createdAt}
              onChange={(v) => setFilter("createdAt", v)}
              placeholder={t("common:actions.select")}
              valueFormat="YYYY-MM-DD"
              size="sm"
              clearable
            />
          </Grid.Col>

          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select
              label={t("order:backOrder.poType")}
              value={filters.purchaseType}
              onChange={(v) => setFilter("purchaseType", v)}
              data={orderPurchaseTypes.map((o) => ({ label: o.name, value: o.code }))}
              placeholder={t("common:actions.select")}
              clearable
              size="sm"
            />
          </Grid.Col>

          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select
              label={t("order:backOrder.poStatus")}
              value={filters.orderStatus}
              onChange={(v) => setFilter("orderStatus", v)}
              data={orderStatus.map((o) => ({ label: o.sentName!, value: o.code }))}
              placeholder={t("common:actions.select")}
              clearable
              size="sm"
            />
          </Grid.Col>

          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <DatePickerInput
              label={t("order:backOrder.deliveryDate")}
              type="range"
              value={filters.deliveryDate}
              onChange={(v) => setFilter("deliveryDate", v)}
              placeholder={t("common:actions.select")}
              valueFormat="YYYY-MM-DD"
              size="sm"
              clearable
            />
          </Grid.Col>

          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select
              label={t("order:backOrder.deliveryStatus")}
              value={filters.deliveryNoteStatus}
              onChange={(v) => setFilter("deliveryNoteStatus", v)}
              data={deliveryNoteStatus.map((o) => ({ label: o.name, value: o.code }))}
              placeholder={t("common:actions.select")}
              clearable
              size="sm"
            />
          </Grid.Col>

          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select
              label={t("order:backOrder.paymentStatus")}
              value={filters.paymentStatus}
              onChange={(v) => setFilter("paymentStatus", v)}
              data={paymentStatus.map((o) => ({ label: o.name, value: o.code }))}
              placeholder={t("common:actions.select")}
              clearable
              size="sm"
            />
          </Grid.Col>

          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Select
              label={t("order:backOrder.invoiceStatus")}
              value={filters.invoiceStatus}
              onChange={(v) => setFilter("invoiceStatus", v)}
              data={invoiceStatus.map((o) => ({ label: o.name, value: o.code }))}
              placeholder={t("common:actions.select")}
              clearable
              size="sm"
            />
          </Grid.Col>

          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <TextInput
              label={t("order:backOrder.buyer")}
              value={filters.senderBusinessPartnerRefCodeBusinessName}
              onChange={(e) => setFilter("senderBusinessPartnerRefCodeBusinessName", e.target.value)}
              placeholder={t("common:actions.search")}
              size="sm"
            />
          </Grid.Col>
        </Grid>

        <Flex justify="flex-end" gap="sm" mt="md">
          <Button variant="light" color="gray" size="sm" radius="md" leftSection={<BroomIcon size={16} />} onClick={clearFilters}>
            {t("common:actions.clear")}
          </Button>
          <Button variant="filled" color="dark" size="sm" radius="md" leftSection={<MagnifyingGlassIcon size={16} />} onClick={onSearch}>
            {t("common:actions.search")}
          </Button>
        </Flex>
      </Paper>
    </Collapse>
  )
}
